import { Component, type ErrorInfo, type ReactNode } from "react";
import * as Sentry from "@sentry/react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

interface Props {
  children: ReactNode;
  fallbackTitle?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    Sentry.withScope((scope) => {
      scope.setExtra("componentStack", info.componentStack);
      Sentry.captureException(error);
    });
    // Log in PRODUCTION too, not just DEV.
    //
    // This used to be `if (import.meta.env.DEV)`. In a production build that
    // is false, so a crashed page logged nothing to the console and showed
    // nothing on screen — the fallback said "An unexpected error occurred"
    // and that was the entire available information. Debugging a live crash
    // meant either reproducing it against a dev build or digging through
    // Sentry, neither of which the person actually looking at the broken
    // screen can do.
    //
    // On 2026-09-10 this cost a full debugging cycle on the Dynamic Pricing
    // page: the console was checked, found empty, and the crash stayed
    // unexplained. A client-side stack trace is not sensitive — it is our
    // own bundled code — and the console is where anyone looks first.
    console.error("[ErrorBoundary]", error, info.componentStack);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[320px] gap-4 p-8">
          <div className="rounded-full bg-destructive/10 p-4">
            <AlertTriangle className="h-8 w-8 text-destructive" />
          </div>
          <div className="text-center space-y-1">
            <h2 className="text-lg font-semibold text-foreground">
              {this.props.fallbackTitle ?? "Something went wrong"}
            </h2>
            <p className="text-sm text-muted-foreground max-w-md">
              An unexpected error occurred. Please try again or refresh the page.
            </p>
            {/* Shown in production too, behind a disclosure.
                A screenshot of a crashed page is the most common bug report
                we get, and until now it carried no information at all — the
                message was DEV-only, so the report was always "it says
                something went wrong". Collapsed by default so a merchant
                sees a clean error; one click gets whoever is helping them
                the actual message. It is our own bundled client code, not
                merchant data. */}
            {this.state.error && (
              <details className="mt-3 text-left max-w-lg mx-auto">
                <summary className="text-xs text-muted-foreground/70 cursor-pointer select-none hover:text-muted-foreground">
                  Technical details
                </summary>
                <p className="text-xs text-muted-foreground/60 font-mono mt-2 break-words whitespace-pre-wrap">
                  {this.state.error.message}
                </p>
              </details>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={this.handleRetry}>
              <RefreshCw className="h-4 w-4 mr-2" /> Try Again
            </Button>
            <Button variant="outline" size="sm" onClick={() => window.location.reload()}>
              Refresh Page
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
